// The ONE web RSVP write path (cross-check.md F6): Events list + details, Who's going,
// Profile club events, Chats EventShareCard and the club page all toggle through this.
//
//   const { toggle, busyIds, isBusy, confirmNode } = useRsvpToggle({ confirmLeave, onCount });
//   await toggle(event)            // -> { ok, going } | null when refused / cancelled
//   {confirmNode}                  // render once; the "Leave Event" dialog when confirmLeave
//
// - `was` (currently going) comes from DataContext.goingEventIds unless passed: toggle(event, { was }).
// - Optimistic: DataContext.patchUserEvent + onCount(eventId, +1 | -1) at once, rolled back with a toast
//   on error (BUG-PATTERNS L40). On success bumpEventsVersion() reloads userEvents (reconcile);
//   on failure loadUserEvents() re-issues the load the optimistic patch superseded.
// - Refuses (null) while DataContext.userEventsLoaded is false and no `was` is passed.
// - Refuses to cancel a checked-in RSVP (the rules deny that delete; EventDetailsScreen.js:453).
// - Per-event lock (synchronous, module-level Set keyed uid:eventId) shared by EVERY hook instance in
//   this tab, so a double click or two cards for the same event (duplicate event-share cards in one
//   chat) send one write. Other tabs of this browser are serialized by a Web Locks lock on the same
//   key (refused, not queued, while held). Other DEVICES are NOT covered: EventService.rsvpToEvent's
//   existence check runs outside the transaction read set with a random doc id (needs a
//   deterministic attendee id or a callable server-side; deferred, it spans service + functions +
//   rules + a dedup migration).
// - signedInAs(uid) right before the write AND before applying the result (W1 rule).
// - The success toast / success modal is the caller's job (`ok && going`).
import { createElement, useCallback, useEffect, useRef, useState } from "react";
import { EventService } from "../../../services/eventService";
import ConfirmModal from "../components/manage/ConfirmModal";
import { useToast } from "../components/Toast";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { signedInAs } from "../lib/session";

const RSVP_FAILED = "Failed to update your RSVP. Please try again.";
const inFlight = new Set(); // `${uid}:${eventId}` writes in progress in this tab, across hook instances
const lockKey = (uid, id) => `${uid}:${id}`;

// Cross-tab guard: run `fn` only if no other tab of this origin holds the RSVP lock for `key`;
// resolves null when it is held. Browsers without Web Locks fall back to the in-tab lock only.
function withCrossTabLock(key, fn) {
  const locks = typeof navigator !== "undefined" ? navigator.locks : null;
  if (!locks?.request) return fn();
  return locks.request(`vl-rsvp:${key}`, { ifAvailable: true }, (lock) => (lock ? fn() : null));
}

// `leaveTestId` names the leave dialog (`${leaveTestId}.confirm` / `.cancel`, mobile eventDetails.leaveConfirm).
export default function useRsvpToggle({ confirmLeave = false, onCount, leaveTestId = "eventDetails.leaveConfirm" } = {}) {
  const { user } = useAuth();
  const uid = user?.uid;
  const toast = useToast();
  const { userEvents, userEventsLoaded, goingEventIds, checkedInEventIds, patchUserEvent, bumpEventsVersion, loadUserEvents } = useData();
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [pending, setPending] = useState(null); // { event } while the leave dialog is open
  const resolveRef = useRef(null); // resolves the open dialog's promise
  const lockRef = useRef(new Set());
  const aliveRef = useRef(true);
  const onCountRef = useRef(onCount);
  onCountRef.current = onCount;
  const rowsRef = useRef(userEvents);
  rowsRef.current = userEvents;

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      resolveRef.current?.(false);
      resolveRef.current = null;
    };
  }, []);

  const setBusy = useCallback((id, on) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  // The write itself; `write` holds the in-tab and cross-tab locks around it.
  const commit = useCallback(async (event, was) => {
    const id = event.id;
    // Re-checked here too: the cross-tab lock wait can span an account switch.
    if (!signedInAs(uid)) return null;
    lockRef.current.add(id);
    setBusy(id, true);
    const before = rowsRef.current.find((r) => r && r.event_id === id) || null;
    const delta = was ? -1 : 1;
    patchUserEvent(id, was ? null : { event_id: id, user_id: uid, status: "attending", checked_in: false, event });
    onCountRef.current?.(id, delta);
    try {
      const { error } = was ? await EventService.cancelRsvp(id, uid) : await EventService.rsvpToEvent(id, uid, "attending");
      if (!signedInAs(uid)) return null; // account switched mid-write: the remount drops this state
      if (error) throw error;
      bumpEventsVersion();
      return { ok: true, going: !was };
    } catch (e) {
      if (__DEV__) console.error("RSVP toggle failed:", e);
      if (signedInAs(uid)) {
        patchUserEvent(id, before);
        onCountRef.current?.(id, -delta);
        toast(RSVP_FAILED, "alert");
        // The optimistic patch superseded any in-flight attendance load (possibly the first
        // one): reload so userEventsLoaded / userEventsError are settled, not stuck unset.
        loadUserEvents();
      }
      return { ok: false, going: was };
    } finally {
      lockRef.current.delete(id);
      if (aliveRef.current) setBusy(id, false);
    }
  }, [uid, toast, patchUserEvent, bumpEventsVersion, loadUserEvents, setBusy]);

  const write = useCallback(async (event, was) => {
    const id = event.id;
    if (!signedInAs(uid)) { toast("You signed out or switched accounts.", "alert"); return null; }
    const key = lockKey(uid, id);
    if (inFlight.has(key)) return null; // another card / page is already writing this RSVP
    inFlight.add(key);
    try {
      return await withCrossTabLock(key, () => commit(event, was));
    } finally {
      inFlight.delete(key);
    }
  }, [uid, toast, commit]);

  const toggle = useCallback(async (event, opts = {}) => {
    const id = event?.id;
    if (!uid || !id || lockRef.current.has(id) || inFlight.has(lockKey(uid, id))) return null;
    // Until attendance has loaded, "not in goingEventIds" is unknown, not "not going": refuse
    // rather than send an RSVP (and +1 the count) for an event the user may already attend.
    if (typeof opts.was !== "boolean" && !userEventsLoaded) return null;
    const was = typeof opts.was === "boolean" ? opts.was : goingEventIds.has(id);
    if (was && checkedInEventIds.has(id)) {
      toast("You're checked in, so this RSVP can't be removed.", "alert");
      return null;
    }
    if (was && confirmLeave) {
      resolveRef.current?.(false); // only one dialog at a time
      const ok = await new Promise((resolve) => { resolveRef.current = resolve; setPending({ event }); });
      if (!ok || !aliveRef.current || lockRef.current.has(id) || inFlight.has(lockKey(uid, id))) return null;
    }
    return write(event, was);
  }, [uid, userEventsLoaded, goingEventIds, checkedInEventIds, confirmLeave, toast, write]);

  const close = useCallback((ok) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setPending(null);
    resolve?.(ok);
  }, []);

  const confirmNode = pending
    ? createElement(ConfirmModal, {
      title: "Leave Event",
      confirmLabel: "Leave",
      danger: true,
      testId: leaveTestId,
      onConfirm: () => close(true),
      onClose: () => close(false),
    }, "Are you sure you want to leave this event?")
    : null;

  const isBusy = useCallback((id) => busyIds.has(id), [busyIds]);

  return { toggle, busyIds, isBusy, confirmNode };
}
